export enum ActionTypes {
  CREATE = 1,
  UPDATE = 2,
  LIST = 3,
  AMEND = 4,
  QC_DEVIATION = 10
}

export enum StatusTypes {
  DRAFT = 1,
  SUBMIT = 2,
  RECEIVED = 3,
  SENDBACK = 4,
  APPROVED = 5,
  REJECT = 6
}

export enum BindValueType {
  SINGLE_VALUE = 'SINGLE_VALUE',
  MULTI_VALUE = 'MULTI_VALUE'
}

export interface BindKeys {
  isServerSided: boolean;
  value: string;
  one: string;
  two?: string;
  type: BindValueType;
}

export interface DropdownOptionLike {
  [key: string]: unknown;
}

export interface DropdownInfoConfig {
  name: string;
  valueKey: string;
  display1: string;
  display2?: string;
  label: string;
  placeholder?: string;
  options?: DropdownOptionLike[];
  apiInfo?: ApiEndpointKey | ApiEndpointConfig;
  status?: number;
  showStatusSwitch?: boolean;
  isSearchable?: boolean;
  searchReadonly?: boolean;
  extraOptions?: DropdownOptionLike[];
  hideLoadingMessage?: boolean;
  initialValue?: unknown;
}

export interface ApiEndpointConfig {
  service: string;
  apiPath: string;
  actionType: ActionTypes | StatusTypes | number;
  isAuthNeeded?: boolean;
  filters?: Record<string, unknown>;
  needToRefresh?: boolean;
  bindKeys?: BindKeys;
  dropdownInfo?: Partial<DropdownInfoConfig>;
}

export type ApiEndpointKey =
  | 'AUTH_LOGIN'
  | 'AUTH_REFRESH'
  | 'AUTH_REGISTER_OTP'
  | 'AUTH_PASSWORD_RESET_OTP'
  | 'AUTH_PASSWORD_RESET'
  | 'AUTH_REGISTER'
  | 'USER_ACCESS_BY_ROLE'
  | 'SUBSCRIPTION_INFO'
  | 'SUBSCRIPTION_SUMMARY'
  | 'PACKAGE_LIST'
  | 'PACKAGE_HISTORY_LIST'
  | 'PACKAGE_CREATE'
  | 'PACKAGE_UPDATE'
  | 'PACKAGE_DELETE'
  | 'ASSIGN_PACKAGE_LIST'
  | 'ASSIGN_PACKAGE_HISTORY_LIST'
  | 'ASSIGN_PACKAGE_CREATE'
  | 'ASSIGN_PACKAGE_UPDATE'
  | 'ASSIGN_PACKAGE_DELETE'
  | 'ASSIGN_PACKAGE_RECEIPT_SEND_EMAIL'
  | 'APP_USER_LIST'
  | 'APP_USER_EMPLOYEE_CREATE'
  | 'APP_USER_EMPLOYEE_UPDATE'
  | 'APP_USER_EMPLOYEE_DELETE'
  | 'APP_USER_PROFILE_BASIC_UPDATE'
  | 'APP_USER_PROFILE_OTP'
  | 'APP_USER_PROFILE_EMAIL_OLD_OTP'
  | 'APP_USER_PROFILE_EMAIL_NEW_OTP'
  | 'APP_USER_PROFILE_EMAIL_UPDATE'
  | 'APP_USER_PROFILE_PASSWORD_UPDATE'
  | 'FILE_PREVIEW_URL'
  | 'FILE_PREVIEW_URL_ATTACHMENT'
  | 'FILE_MANAGE_ATTACHMENT'
  | 'HOUSE_CREATE'
  | 'HOUSE_UPDATE'
  | 'HOUSE_DELETE'
  | 'HOUSE_LIST'
  | 'FLAT_CREATE'
  | 'FLAT_UPDATE'
  | 'FLAT_LIST'
  | 'FLAT_DELETE'
  | 'FLAT_RENTER_MAPPING_LIST'
  | 'FLAT_RENTER_MAPPING_CREATE'
  | 'FLAT_RENTER_MAPPING_UPDATE'
  | 'FLAT_RENTER_MAPPING_DELETE'
  | 'PAYMENT_PENDING_LIST'
  | 'YEARLY_PAYMENT_SUMMARY'
  | 'RENTER_LIST'
  | 'RENTER_CREATE'
  | 'RENTER_UPDATE'
  | 'RENTER_DELETE'
  | 'RENTER_RESTORE'
  | 'EXPENSE_LIST'
  | 'EXPENSE_CREATE'
  | 'EXPENSE_UPDATE'
  | 'EXPENSE_DELETE'
  | 'EXPENSE_HEAD_LIST'
  | 'YEARLY_EXPENSE_SUMMARY'
  | 'PAYMENT_LIST'
  | 'PAYMENT_CREATE'
  | 'PAYMENT_UPDATE'
  | 'PAYMENT_DELETE'
  | 'RECEIPT_SEND_EMAIL'
  | 'RECEIPT_SEND_WHATSAPP'
  | 'FILE_UPLOAD';

export const ApiEndpoints: Record<ApiEndpointKey, ApiEndpointConfig> = {
  AUTH_LOGIN: {
    service: 'AUTH_LOGIN',
    apiPath: 'auth/token',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: false
  },
  AUTH_REFRESH: {
    service: 'AUTH_REFRESH',
    apiPath: 'auth/refresh',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: false
  },
  AUTH_REGISTER_OTP: {
    service: 'AUTH_REGISTER_OTP',
    apiPath: 'auth/register/otp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: false
  },
  AUTH_PASSWORD_RESET_OTP: {
    service: 'AUTH_PASSWORD_RESET_OTP',
    apiPath: 'auth/password/reset/otp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: false
  },
  AUTH_PASSWORD_RESET: {
    service: 'AUTH_PASSWORD_RESET',
    apiPath: 'auth/password/reset',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: false
  },
  AUTH_REGISTER: {
    service: 'USER_CREATE',
    apiPath: 'auth/register',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: false
  },
  USER_ACCESS_BY_ROLE: {
    service: 'USER_ACCESS_LIST',
    apiPath: 'setup/user-access-by-role',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  SUBSCRIPTION_INFO: {
    service: 'SUBSCRIPTION_MENU',
    apiPath: 'setup/subscription-info',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  SUBSCRIPTION_SUMMARY: {
    service: 'USER_ACCESS_LIST',
    apiPath: 'setup/subscription-summary',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  PACKAGE_LIST: {
    service: 'PACKAGE_LIST',
    apiPath: 'package/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  PACKAGE_HISTORY_LIST: {
    service: 'PACKAGE_LIST',
    apiPath: 'package/history-list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  PACKAGE_CREATE: {
    service: 'PACKAGE_CREATE',
    apiPath: 'package/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  PACKAGE_UPDATE: {
    service: 'PACKAGE_CREATE',
    apiPath: 'package/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  PACKAGE_DELETE: {
    service: 'PACKAGE_DELETE',
    apiPath: 'package/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_LIST: {
    service: 'PACKAGE_LIST',
    apiPath: 'assign-package/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_HISTORY_LIST: {
    service: 'PACKAGE_LIST',
    apiPath: 'assign-package/history-list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_CREATE: {
    service: 'PACKAGE_CREATE',
    apiPath: 'assign-package/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_UPDATE: {
    service: 'PACKAGE_CREATE',
    apiPath: 'assign-package/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_DELETE: {
    service: 'PACKAGE_DELETE',
    apiPath: 'assign-package/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  ASSIGN_PACKAGE_RECEIPT_SEND_EMAIL: {
    service: 'RECEIPT_SEND_EMAIL',
    apiPath: 'assign-package/receipt/email',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  APP_USER_LIST: {
    service: 'USER_LIST',
    apiPath: 'auth/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  APP_USER_EMPLOYEE_CREATE: {
    service: 'EMPLOYEE_CREATE',
    apiPath: 'auth/add_employee',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  APP_USER_EMPLOYEE_UPDATE: {
    service: 'EMPLOYEE_CREATE',
    apiPath: 'auth/add_employee',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  APP_USER_EMPLOYEE_DELETE: {
    service: 'EMPLOYEE_DELETE',
    apiPath: 'auth/add_employee',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_BASIC_UPDATE: {
    service: 'USER_PROFILE_BASIC_UPDATE',
    apiPath: 'auth/profile/basic',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_OTP: {
    service: 'USER_PROFILE_OTP',
    apiPath: 'auth/profile/otp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_EMAIL_OLD_OTP: {
    service: 'USER_PROFILE_EMAIL_OLD_OTP',
    apiPath: 'auth/profile/email/old-otp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_EMAIL_NEW_OTP: {
    service: 'USER_PROFILE_EMAIL_NEW_OTP',
    apiPath: 'auth/profile/email/new-otp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_EMAIL_UPDATE: {
    service: 'USER_PROFILE_EMAIL_UPDATE',
    apiPath: 'auth/profile/email',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  APP_USER_PROFILE_PASSWORD_UPDATE: {
    service: 'USER_PROFILE_PASSWORD_UPDATE',
    apiPath: 'auth/profile/password',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  FILE_PREVIEW_URL: {
    service: 'FILE_PREVIEW_URL',
    apiPath: 'files/preview-url',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  FILE_PREVIEW_URL_ATTACHMENT: {
    service: 'FILE_PREVIEW_URL_ATTACHMENT',
    apiPath: 'files/preview-url-attachment',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  FILE_MANAGE_ATTACHMENT: {
    service: 'FILE_MANAGE_ATTACHMENT',
    apiPath: 'files/manage-attachment',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  HOUSE_CREATE: {
    service: 'HOUSE_CREATE',
    apiPath: 'house/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  HOUSE_UPDATE: {
    service: 'HOUSE_CREATE',
    apiPath: 'house/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  HOUSE_DELETE: {
    service: 'HOUSE_DELETE',
    apiPath: 'house/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  HOUSE_LIST: {
    service: 'HOUSE_LIST',
    apiPath: 'house/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true,
    dropdownInfo: {
      name: 'houseId',
      valueKey: 'id',
      display1: 'houseName',
      display2: 'houseNo',
      label: 'filter.house',
      placeholder: 'placeholder.selectHouse',
      apiInfo: 'HOUSE_LIST',
      isSearchable: true
    }
  },
  FLAT_LIST: {
    service: 'FLAT_LIST',
    apiPath: 'flat/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true,
    dropdownInfo: {
      name: 'flatId',
      valueKey: 'id',
      display1: 'flatName',
      label: 'filter.flat',
      placeholder: 'placeholder.selectFlat',
      apiInfo: 'FLAT_LIST',
      isSearchable: true
    }
  },
  FLAT_CREATE: {
    service: 'FLAT_CREATE',
    apiPath: 'flat/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  FLAT_UPDATE: {
    service: 'FLAT_CREATE',
    apiPath: 'flat/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  FLAT_DELETE: {
    service: 'FLAT_DELETE',
    apiPath: 'flat/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  FLAT_RENTER_MAPPING_LIST: {
    service: 'FLAT_RENTER_MAPPING_LIST',
    apiPath: 'flat-renter/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  FLAT_RENTER_MAPPING_CREATE: {
    service: 'FLAT_RENTER_MAPPING_CREATE',
    apiPath: 'flat-renter/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  FLAT_RENTER_MAPPING_UPDATE: {
    service: 'FLAT_RENTER_MAPPING_CREATE',
    apiPath: 'flat-renter/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  FLAT_RENTER_MAPPING_DELETE: {
    service: 'FLAT_RENTER_MAPPING_DELETE',
    apiPath: 'flat-renter/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  PAYMENT_PENDING_LIST: {
    service: 'PAYMENT_PENDING_LIST',
    apiPath: 'flat-renter/payment-pending-list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  RENTER_LIST: {
    service: 'RENTER_LIST',
    apiPath: 'renter/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  RENTER_CREATE: {
    service: 'RENTER_CREATE',
    apiPath: 'renter/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  RENTER_UPDATE: {
    service: 'RENTER_CREATE',
    apiPath: 'renter/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  RENTER_DELETE: {
    service: 'RENTER_DELETE',
    apiPath: 'renter/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  RENTER_RESTORE: {
    service: 'RENTER_DELETE',
    apiPath: 'renter/restore',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  EXPENSE_LIST: {
    service: 'EXPENSE_LIST',
    apiPath: 'expense/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  EXPENSE_CREATE: {
    service: 'EXPENSE_CREATE',
    apiPath: 'expense/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  EXPENSE_UPDATE: {
    service: 'EXPENSE_CREATE',
    apiPath: 'expense/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  EXPENSE_DELETE: {
    service: 'EXPENSE_DELETE',
    apiPath: 'expense/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  EXPENSE_HEAD_LIST: {
    service: 'EXPENSE_HEAD_LIST',
    apiPath: 'expense-head/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true,
    dropdownInfo: {
      name: 'expenseHeadId',
      valueKey: 'id',
      display1: 'name',
      label: 'filter.expenseHead',
      placeholder: 'placeholder.selectExpenseHead',
      apiInfo: 'EXPENSE_HEAD_LIST',
      isSearchable: true
    }
  },
  YEARLY_EXPENSE_SUMMARY: {
    service: 'YEARLY_EXPENSE_SUMMARY',
    apiPath: 'expense/yearly-expense-summary',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  PAYMENT_LIST: {
    service: 'PAYMENT_LIST',
    apiPath: 'payment/list',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  PAYMENT_CREATE: {
    service: 'PAYMENT_CREATE',
    apiPath: 'payment/add',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  PAYMENT_UPDATE: {
    service: 'PAYMENT_CREATE',
    apiPath: 'payment/add',
    actionType: ActionTypes.UPDATE,
    isAuthNeeded: true
  },
  PAYMENT_DELETE: {
    service: 'PAYMENT_DELETE',
    apiPath: 'payment/delete',
    actionType: ActionTypes.AMEND,
    isAuthNeeded: true
  },
  RECEIPT_SEND_EMAIL: {
    service: 'RECEIPT_SEND_EMAIL',
    apiPath: 'receipt-delivery/email',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  RECEIPT_SEND_WHATSAPP: {
    service: 'RECEIPT_SEND_WHATSAPP',
    apiPath: 'receipt-delivery/whatsapp',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  },
  YEARLY_PAYMENT_SUMMARY: {
    service: 'YEARLY_PAYMENT_SUMMARY',
    apiPath: 'payment/yearly-payment-summary',
    actionType: ActionTypes.LIST,
    isAuthNeeded: true
  },
  FILE_UPLOAD: {
    service: 'FILE_UPLOAD',
    apiPath: 'files/upload',
    actionType: ActionTypes.CREATE,
    isAuthNeeded: true
  }
} as const;

export type ApiEndpointValue = (typeof ApiEndpoints)[keyof typeof ApiEndpoints];
